/**
 * Shared codec-group helpers used by the codec group editor and the codec group
 * page (list + details). Kept in one place so the editor and page stay in sync.
 */

import { Music, Video, Settings, type LucideIcon } from 'lucide-react';
import type { Sip } from '@sbclite/core';
import { SBC_SUPPORTED_CODECS } from '@sbclite/core/sip/constants';

type CodecGroupInfo = Sip.CodecGroupInfo;
type CodecDefinition = CodecGroupInfo['codecs'][number];

export const INSTALLED_CODEC_OPTIONS: ReadonlyArray<{
  name: string;
  codecType: CodecDefinition['codecType'];
}> = SBC_SUPPORTED_CODECS.map((codecName) => ({
  name: codecName,
  codecType: 'audio',
}));

/** Returns the Lucide icon component for a codec type (caller renders it). */
export function getCodecTypeIcon(codecType: 'audio' | 'video' | 'fax'): LucideIcon {
  switch (codecType) {
    case 'audio': return Music;
    case 'video': return Video;
    case 'fax': return Settings;
    default: return Music;
  }
}

function toCodecId(codecName: string): string {
  return codecName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

export function buildCodecRows(
  selectedCodecNames: string[],
  existingCodecs?: CodecGroupInfo['codecs']
): CodecGroupInfo['codecs'] {
  const existingByName = new Map((existingCodecs ?? []).map((codec) => [codec.codecName.toUpperCase(), codec]));

  return selectedCodecNames.map((codecName, index) => {
    const existing = existingByName.get(codecName.toUpperCase());
    const installed = INSTALLED_CODEC_OPTIONS.find((option) => option.name === codecName);

    return {
      codecId: existing?.codecId ?? toCodecId(codecName),
      codecName,
      codecType: existing?.codecType ?? installed?.codecType ?? 'audio',
      priority: index + 1,
      enabled: true,
      parameters: existing?.parameters,
    };
  });
}
